/** Exceptions & Escalations Centre (/exception-centre) — auto-detected operational risks, every one actionable. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Settings2, ShieldAlert, ChevronDown, ChevronRight } from "lucide-react";
import { ExceptionRow } from "./ExceptionRow";
import { ActDialog, DetailSheet, SettingsDialog } from "./ExceptionDialogs";
import { SEV, CAT_LABEL } from "./exceptionUtils";

const SevTile = ({ sev, n, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`sev-${sev}`}
    className={`rounded-lg border px-3 py-2 text-left transition-colors ${active ? SEV[sev].cls : "bg-white border-slate-200 hover:border-slate-400"}`}>
    <div className={`text-[10px] font-semibold uppercase tracking-wider ${active ? "opacity-80" : "text-slate-500"}`}>{sev}</div>
    <div className="font-mono text-xl font-bold leading-tight">{n ?? 0}</div>
  </button>
);

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{children}</button>
);

const Sel = ({ value, onChange, children, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700">{children}</select>
);

export default function ExceptionCentre() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [detail, setDetail] = useState(null);
  const [act, setAct] = useState(null);
  const [settings, setSettings] = useState(false);
  const [expanded, setExpanded] = useState({});

  const params = useMemo(() => ({ scope: sp.get("scope") || "auto", severity: sp.get("severity") || "", category: sp.get("category") || "",
    status: sp.get("status") || "", code: sp.get("code") || "", owner: sp.get("owner") || "", q }), [sp, q]);
  const setP = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/exception-centre/board", { params }); setData(d); }
    catch (e) { if (e.response?.status === 403) setDenied(true); else toast.error("Could not load exceptions"); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  if (denied) return <div className="py-24 text-center text-sm text-slate-500" data-testid="exc-denied">Exceptions & Escalations is available to Suntronix staff only.</div>;
  if (!data) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Scanning live records…</div>;

  const c = data.counters;
  const onAct = async (kind, item) => {
    if (kind === "start") {
      try { await api.post("/exception-centre/start", { key: item.key, category: item.category, title: item.title }); toast.success("Marked in progress"); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
      return;
    }
    setAct({ kind, item });
  };
  const groups = data.items.reduce((acc, it) => { (acc[it.category] = acc[it.category] || []).push(it); return acc; }, {});
  const rank = { CRITICAL: 0, HIGH: 1, ATTENTION: 2, INFO: 3 };
  const worst = (cat) => Math.min(...groups[cat].map((i) => rank[i.severity]));
  const order = Object.keys(groups).sort((a, b) => worst(a) - worst(b) || groups[b].length - groups[a].length);

  return (
    <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4" data-testid="exception-centre">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-rose-600">Enterprise OS · {data.date}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><ShieldAlert className="h-6 w-6 text-rose-600" />Exceptions & Escalations</h1>
          <p className="text-sm text-slate-500 mt-0.5">{c.total} operational risks detected across 21 rules · {c.overdue} past due · {c.resolved_today} resolved today. Every row has an owner, a due date and a next action.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="exc-refresh"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Rescan</Button>
          <Button variant="outline" size="sm" onClick={() => setSettings(true)} data-testid="exc-settings"><Settings2 className="h-3.5 w-3.5 mr-1" />Thresholds</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {["CRITICAL", "HIGH", "ATTENTION", "INFO"].map((s) => <SevTile key={s} sev={s} n={c[s]} active={params.severity === s} onClick={() => setP("severity", params.severity === s ? "" : s)} />)}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          <Chip on={params.status === ""} onClick={() => setP("status", "")} testId="status-all">All open {c.total}</Chip>
          <Chip on={params.status === "OPEN"} onClick={() => setP("status", "OPEN")} testId="status-open">New {c.open}</Chip>
          <Chip on={params.status === "IN_PROGRESS"} onClick={() => setP("status", "IN_PROGRESS")} testId="status-inprogress">In progress {c.in_progress}</Chip>
          <Chip on={params.status === "ESCALATED"} onClick={() => setP("status", "ESCALATED")} testId="status-escalated">Escalated {c.escalated}</Chip>
        </div>
        <span className="h-4 w-px bg-slate-200 hidden sm:block" />
        <Sel value={params.scope} onChange={(v) => setP("scope", v)} testId="scope-select">
          <option value="auto">{data.is_management ? "All teams" : "My team"}</option>
          <option value="mine">Assigned to me</option>
          {data.is_management && <option value="all">All teams</option>}
        </Sel>
        <Sel value={params.category} onChange={(v) => setP("category", v)} testId="category-select">
          <option value="">All categories</option>
          {Object.entries(data.categories).map(([k, l]) => <option key={k} value={k}>{CAT_LABEL[k] || l} ({data.by_category[k] || 0})</option>)}
        </Sel>
        <Sel value={params.code} onChange={(v) => setP("code", v)} testId="code-select">
          <option value="">All rules</option>
          {Object.entries(data.codes).map(([k, l]) => <option key={k} value={k}>{l} ({data.by_code[k] || 0})</option>)}
        </Sel>
        <Input placeholder="Search customer, record, reason…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-56 text-[12px]" data-testid="exc-search" />
        <Input placeholder="Owner" value={params.owner} onChange={(e) => setP("owner", e.target.value)} className="h-8 w-32 text-[12px]" data-testid="exc-owner-filter" />
      </div>

      {data.items.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white py-16 text-center" data-testid="exc-empty">
          <ShieldAlert className="h-8 w-8 mx-auto text-emerald-400" />
          <div className="mt-2 text-sm font-semibold text-slate-700">No exceptions match these filters</div>
          <div className="text-[12px] text-slate-500">Clear a filter or rescan.</div>
        </div>
      ) : order.map((cat) => (
        <section key={cat} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`exc-group-${cat}`}>
          <button type="button" onClick={() => setCollapsed((s) => ({ ...s, [cat]: !s[cat] }))} className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 text-left" data-testid={`exc-group-toggle-${cat}`}>
            {collapsed[cat] ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
            <span className="text-[12px] font-bold uppercase tracking-wider text-slate-800">{CAT_LABEL[cat] || cat}</span>
            <span className="font-mono text-[11px] text-slate-500">{groups[cat].length}</span>
            <span className="ml-auto flex gap-1">{["CRITICAL", "HIGH", "ATTENTION", "INFO"].map((s) => { const n = groups[cat].filter((i) => i.severity === s).length; return n ? <span key={s} className={`rounded border px-1.5 font-mono text-[10px] ${SEV[s].soft}`}>{n} {s[0]}</span> : null; })}</span>
          </button>
          {!collapsed[cat] && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <tr>{["Severity", "Exception · Reason · Recommended action", "Customer", "Owner", "Age", "Due", "Escalation", "Status", ""].map((h) => <th key={h} className="px-3 py-1.5 whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>{(expanded[cat] ? groups[cat] : groups[cat].slice(0, 40)).map((it) => <ExceptionRow key={it.key} it={it} onOpen={setDetail} onAct={onAct} />)}</tbody>
              </table>
              {groups[cat].length > 40 && !expanded[cat] && (
                <button type="button" onClick={() => setExpanded((s) => ({ ...s, [cat]: true }))} className="w-full py-2 text-[12px] text-sky-700 hover:bg-slate-50 border-t border-slate-100" data-testid={`exc-group-more-${cat}`}>Show all {groups[cat].length}</button>
              )}
            </div>
          )}
        </section>
      ))}

      <DetailSheet item={detail} onClose={() => setDetail(null)} onAct={(k, it) => { if (k === "start") onAct(k, it); else setAct({ kind: k, item: it }); }} />
      <ActDialog act={act} onClose={() => setAct(null)} onDone={() => { setAct(null); setDetail(null); load(); }} />
      <SettingsDialog open={settings} onClose={() => setSettings(false)} onSaved={() => { setSettings(false); load(); }} />
    </div>
  );
}
